import { useEffect, useState } from "react";
import { Card, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { ClientService } from "../../component/services/client.service";
import { makeParams } from "../../component/api/makeRequest";
import { DateRange } from "../../component/DateRangeSelector/DateRangeSelector";
import { useTheme } from "../../theme/useTheme";
import moment from "moment";

interface HourEntry { hour: number; failures: number; }
interface VendorHourlyStat { vendorName: string; hours: HourEntry[]; }

interface Props { dateRange: DateRange; }

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(h: number) {
    return moment({ hour: h }).format("ha");
}

// Interpolate white → red based on intensity 0–1
function heatColor(intensity: number): string {
    if (intensity === 0) return "#f3f4f6";
    // white(255,255,255) → red(239,68,68)
    const r = Math.round(255 - (255 - 239) * intensity);
    const g = Math.round(255 - (255 - 68) * intensity);
    const b = Math.round(255 - (255 - 68) * intensity);
    return `rgb(${r},${g},${b})`;
}

function textColor(intensity: number): string {
    return intensity > 0.55 ? "#fff" : "#374151";
}

export default function VendorHourlyHeatmap({ dateRange }: Props) {
    const { colors, colorSchemes } = useTheme();
    const [data, setData] = useState<VendorHourlyStat[]>([]);
    const [loading, setLoading] = useState(false);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

    async function fetchData(start: Date, end: Date) {
        setLoading(true);
        setData([]);
        const params = makeParams([
            { index: "start_time", value: moment(start).toISOString() },
            { index: "end_time", value: moment(end).toISOString() },
        ]);
        await ClientService.getVendorHourlyStats(params)
            .then((res: any) => setData(res.data?.data || []))
            .catch((err: any) => console.log(err))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        fetchData(dateRange.startDate, dateRange.endDate);
    }, [dateRange.startDate, dateRange.endDate]);

    // Build vendor→hour map, find global max for colour scaling
    const vendorMaps = data.map(v => {
        const map = new Map(v.hours.map(h => [h.hour, h.failures]));
        return { name: v.vendorName, map };
    });
    const globalMax = Math.max(1, ...data.flatMap(v => v.hours.map(h => h.failures)));

    const CELL_W = 32;
    const CELL_H = 36;
    const LABEL_W = 90;

    return (
        <div className="mt-4 mb-4">
            <div className="mb-3">
                <h6 style={{ fontWeight: 700, color: colors.text.primary, fontSize: "1rem", marginBottom: 2 }}>
                    Time-of-Day Failure Heatmap
                </h6>
                <span style={{ color: colors.text.muted, fontSize: "0.78rem" }}>
                    Distinct failed students by hour (IST) — darker = more failures
                </span>
            </div>

            {loading ? (
                <div className="d-flex align-items-center justify-content-center py-5">
                    <Spinner animation="border" style={{ color: colors.primary.main }} />
                </div>
            ) : data.length === 0 ? (
                <div className="d-flex flex-column align-items-center justify-content-center py-5">
                    <FontAwesomeIcon icon={faClock} style={{ fontSize: "2.5rem", color: colors.border.secondary, marginBottom: "1rem" }} />
                    <span style={{ color: colors.text.muted, fontSize: "0.9rem" }}>No hourly data for the selected range</span>
                </div>
            ) : (
                <Card style={{ ...colorSchemes.card, borderRadius: "14px", border: `1px solid ${colors.border.light}`, overflow: "hidden" }}>
                    <Card.Body className="p-4" style={{ overflowX: "auto" }}>
                        {/* Hour header row */}
                        <div style={{ display: "flex", marginBottom: 4 }}>
                            <div style={{ width: LABEL_W, flexShrink: 0 }} />
                            {HOURS.map(h => (
                                <div
                                    key={h}
                                    style={{
                                        width: CELL_W, flexShrink: 0, textAlign: "center",
                                        fontSize: "0.6rem", color: colors.text.muted, fontWeight: 500,
                                        // Only show every 3rd label to avoid crowding
                                        opacity: h % 3 === 0 ? 1 : 0,
                                    }}
                                >
                                    {formatHour(h)}
                                </div>
                            ))}
                        </div>

                        {/* Vendor rows */}
                        {vendorMaps.map(({ name, map }) => (
                            <div key={name} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                                {/* Vendor label */}
                                <div
                                    style={{
                                        width: LABEL_W, flexShrink: 0,
                                        fontSize: "0.8rem", fontWeight: 600,
                                        color: colors.text.primary,
                                        paddingRight: 8, textAlign: "right",
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}
                                >
                                    {name}
                                </div>

                                {/* Hour cells */}
                                {HOURS.map(h => {
                                    const count = map.get(h) ?? 0;
                                    const intensity = count / globalMax;
                                    const bg = heatColor(intensity);
                                    const fg = textColor(intensity);
                                    return (
                                        <div
                                            key={h}
                                            style={{
                                                width: CELL_W, height: CELL_H, flexShrink: 0,
                                                background: bg,
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: count > 0 ? "0.65rem" : "0",
                                                fontWeight: 600, color: fg,
                                                borderRadius: "4px", margin: "0 1px",
                                                cursor: count > 0 ? "default" : "default",
                                                transition: "transform 0.1s",
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = "scale(1.15)";
                                                e.currentTarget.style.zIndex = "10";
                                                const rect = e.currentTarget.getBoundingClientRect();
                                                setTooltip({
                                                    x: rect.left + rect.width / 2,
                                                    y: rect.top - 8,
                                                    text: `${name} · ${formatHour(h)}: ${count} failure${count !== 1 ? "s" : ""}`,
                                                });
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "scale(1)";
                                                e.currentTarget.style.zIndex = "1";
                                                setTooltip(null);
                                            }}
                                        >
                                            {count > 0 ? count : ""}
                                        </div>
                                    );
                                })}
                            </div>
                        ))}

                        {/* Colour scale legend */}
                        <div className="d-flex align-items-center gap-2 mt-3">
                            <span style={{ fontSize: "0.7rem", color: colors.text.muted }}>Fewer</span>
                            <div style={{ display: "flex", borderRadius: "4px", overflow: "hidden" }}>
                                {Array.from({ length: 10 }, (_, i) => (
                                    <div
                                        key={i}
                                        style={{ width: 20, height: 10, background: heatColor(i / 9) }}
                                    />
                                ))}
                            </div>
                            <span style={{ fontSize: "0.7rem", color: colors.text.muted }}>More</span>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Fixed-position tooltip */}
            {tooltip && (
                <div
                    style={{
                        position: "fixed",
                        left: tooltip.x,
                        top: tooltip.y,
                        transform: "translate(-50%, -100%)",
                        background: "#1f2937",
                        color: "#fff",
                        fontSize: "0.75rem",
                        padding: "4px 10px",
                        borderRadius: "6px",
                        pointerEvents: "none",
                        zIndex: 9999,
                        whiteSpace: "nowrap",
                    }}
                >
                    {tooltip.text}
                </div>
            )}
        </div>
    );
}
